// src/ui/screens/post/DivisionPickerPage.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDivisionsForDistrict } from '../../../utils/divisions';
import { setPickerResult } from '../../../utils/pickerBridge';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

const SEARCH_BAR_HEIGHT = 60;

export const DivisionPickerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const districtName = location.state?.districtName || '';

  const [searchQuery, setSearchQuery] = useState('');
  const [allDivisions, setAllDivisions] = useState([]);
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [searchBarProgress, setSearchBarProgress] = useState(0);

  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── Initialize divisions ──────────────────────────────────────────────────

  useEffect(() => {
    const divisions = getDivisionsForDistrict(districtName);
    setAllDivisions(divisions);
    setFilteredDivisions(divisions);
  }, [districtName]);

  // ── Filter divisions ──────────────────────────────────────────────────────

  const applyFilter = useCallback((query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredDivisions(allDivisions);
      return;
    }
    const filtered = allDivisions.filter((d) =>
      d.name.toLowerCase().includes(q)
    );
    setFilteredDivisions(filtered);
  }, [allDivisions]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    applyFilter(value);
  };

  // ── Scroll handler for search bar animation (mobile only) ────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const progress = Math.min(offset / SEARCH_BAR_HEIGHT, 1);
    setSearchBarProgress(progress);
  }, []);

  // ── Select division ────────────────────────────────────────────────────────
  //
  // Same picker-bridge hand-off as category/district — navigate(-1)'s
  // `state` arg is not reliable in RRv6.

  const handleSelectDivision = (division) => {
    setPickerResult('division', division);
    navigate(-1);
  };

  // ── Go back ───────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  // ── Focus search on mount ─────────────────────────────────────────────────

  useEffect(() => {
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 300);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  const headerHeight = 56 + 60 + 8; // top bar + search bar + gap

  const DivisionIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const EmptyState = ({ compact }) => (
    <div className={compact ? 'flex items-center justify-center h-64' : 'flex items-center justify-center h-96'}>
      <div className="flex flex-col items-center gap-3">
        <svg className="w-13 h-13 text-white/15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-white/35 text-sm font-medium">No divisions found</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged aside from the search bar fix below)
          — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* ── Scrollable List ────────────────────────────────────────────── */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-screen overflow-y-auto"
          style={{ paddingTop: headerHeight, paddingBottom: 32 }}
        >
          {filteredDivisions.length === 0 ? (
            <EmptyState compact />
          ) : (
            <div className="px-4">
              {filteredDivisions.map((division, index) => (
                <div key={division.name}>
                  <button
                    onClick={() => handleSelectDivision(division)}
                    className="w-full flex items-center gap-3 py-3 hover:bg-filatom-card-hover transition-colors rounded-lg px-2"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${COLORS.orange}1F` }}
                    >
                      <DivisionIcon className="w-4.5 h-4.5 text-filatom-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-white text-sm font-medium">{division.name}</span>
                      <div className="text-white/40 text-xs">{division.district} District</div>
                    </div>
                    <svg className="w-5 h-5 text-filatom-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {index < filteredDivisions.length - 1 && (
                    <div className="h-px bg-white/10 mx-2" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Gradient Overlay ───────────────────────────────────────────── */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: headerHeight - SEARCH_BAR_HEIGHT * searchBarProgress,
            background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
          }}
        />

        {/* ── Floating Header ────────────────────────────────────────────── */}
        <div className="absolute top-0 left-0 right-0">
          {/* Top Bar */}
          <div className="flex items-center justify-between px-4 h-14">
            <button
              onClick={handleBack}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#0F1013' }}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1 text-center">
              <span className="text-white text-lg font-bold">Choose a division</span>
              <div className="text-filatom-primary text-xs font-medium">{districtName}</div>
            </div>
            <div className="w-9" />
          </div>

          {/* Search Bar - slides up with scroll */}
          <div
            className="px-4 py-2 transition-all duration-200"
            style={{
              transform: `translateY(-${searchBarProgress * SEARCH_BAR_HEIGHT}px)`,
              opacity: 1 - searchBarProgress,
            }}
          >
            <div
              className="relative h-11 rounded-full overflow-hidden flex items-center"
              style={{ backgroundColor: COLORS.card }}
            >
              <svg
                width="16"
                height="16"
                className="text-gray-400 flex-shrink-0 ml-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search divisions"
                className="w-full h-full bg-transparent text-white text-sm outline-none pl-2 pr-3 placeholder-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up
          Static header (no scroll-hide animation), centered content column,
          card grid instead of a single-column list.
         ══════════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-filatom-bg/90 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-5xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              style={{ backgroundColor: '#0F1013' }}
              aria-label="Go back"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex-1">
              <div className="flex items-center gap-2.5">
                <h1 className="text-white text-2xl font-bold leading-tight">Choose a division</h1>
                {districtName && (
                  <span className="text-filatom-primary text-xs font-semibold bg-filatom-primary/10 px-2.5 py-1 rounded-full">
                    {districtName}
                  </span>
                )}
              </div>
              <p className="text-white/40 text-sm mt-0.5">Narrow down to the specific division</p>
            </div>

            {/* Search */}
            <div className="relative w-72 flex-shrink-0">
              <svg
                width="16"
                height="16"
                className="text-white/35 absolute left-3.5 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search divisions"
                className="w-full h-11 rounded-full bg-filatom-card border border-white/10 text-white text-sm placeholder-white/35 outline-none pl-10 pr-4 focus:border-filatom-primary/60 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-8 py-10">
          {filteredDivisions.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDivisions.map((division) => (
                <button
                  key={division.name}
                  onClick={() => handleSelectDivision(division)}
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-filatom-card border border-white/10 hover:border-filatom-primary/50 hover:bg-white/[0.04] transition-all text-left"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${COLORS.orange}1F` }}
                  >
                    <DivisionIcon className="w-5 h-5 text-filatom-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-white text-base font-semibold truncate">{division.name}</div>
                    <div className="text-white/40 text-sm mt-0.5">{division.district} District</div>
                  </div>

                  <svg
                    className="w-5 h-5 text-filatom-primary flex-shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DivisionPickerPage;